"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
const isomorphic_ws_1 = __importDefault(require("isomorphic-ws"));
const eventemitter3_1 = require("eventemitter3");
class FaucetClient extends eventemitter3_1.EventEmitter {
    static pingIntervalMs = 5000;
    static initialReopenDelayMs = 1000;
    static maxReopenDelayMs = 30000;
    static defaultOptions = {
        host: 'faucet.tradestation.io',
        port: 4537,
        secure: false,
        token: '42'
    };
    options;
    url;
    ping;
    reopenDelay;
    reopenDelayMs = FaucetClient.initialReopenDelayMs;
    socket;
    status = {
        authenticated: false,
        channels: {
            active: 0,
            requested: 0
        },
        connected: false,
        ping: 0
    };
    channels = {};
    nextExpectedDataSequence;
    constructor(options = FaucetClient.defaultOptions) {
        super();
        this.options = options;
        for (const key in FaucetClient.defaultOptions) {
            if (this.options[key] === void 0) {
                this.options[key] = FaucetClient.defaultOptions[key];
            }
        }
        this.url = 'ws' + (this.options.secure ? 's' : '') + '://' +
            this.options.host + ':' + this.options.port;
        this.ping = setInterval(() => {
            this._ping();
        }, FaucetClient.pingIntervalMs);
        setTimeout(() => {
            this._open();
        }, 1);
    }
    currentStatus() {
        return {
            authenticated: this.status.authenticated,
            channels: {
                active: this.status.channels.active,
                requested: this.status.channels.requested
            },
            connected: this.status.connected,
            ping: this.status.ping
        };
    }
    setToken(token) {
        this.options.token = token;
        if (this.status.connected && !this.status.authenticated) {
            this._send('HELLO {"format":"insight-v2","token":"' + this.options.token + '"}');
        }
    }
    start(channel) {
        if (!this.channels[channel]) {
            this.channels[channel] = {
                active: false,
                listeners: 0,
                snapshot: {}
            };
        }
        this.channels[channel].listeners += 1;
        if (this.channels[channel].listeners === 1) {
            this.status.channels.requested += 1;
            this.emit('status', this.currentStatus());
            this._send('START {"channel":"' + channel + '"}');
        }
        return this.channels[channel].snapshot;
    }
    stop(channel) {
        if (!this.channels[channel]) {
            return;
        }
        if (this.channels[channel].listeners > 0) {
            this.channels[channel].listeners -= 1;
            if (this.channels[channel].listeners === 0) {
                this.status.channels.requested -= 1;
                this.emit('status', this.currentStatus());
                this._send('STOP {"channel":"' + channel + '"}');
            }
        }
    }
    close() {
        if (this.reopenDelay) {
            clearTimeout(this.reopenDelay);
            this.reopenDelay = null;
        }
        if (this.ping) {
            clearInterval(this.ping);
            this.ping = null;
        }
        if (!this.socket) {
            return;
        }
        this._onclose(false);
        this.status.authenticated = false;
        this.status.connected = false;
        this.status.channels.active = 0;
        this.status.channels.requested = 0;
        this.channels = {};
        this.emit('status', this.currentStatus());
    }
    _onclose(reopen = true) {
        this.socket.onclose = null;
        this.socket.onerror = null;
        this.socket.onmessage = null;
        this.socket.onopen = null;
        if (this.socket.readyState < 2) {
            try {
                this.socket.close();
            }
            catch {
                // OK to ignore this error.
            }
        }
        this.socket = null;
        if (!reopen) {
            return;
        }
        if (this.status.connected) {
            this.status.authenticated = false;
            this.status.connected = false;
            for (const channel in this.channels) {
                if (this.channels[channel].active) {
                    this.channels[channel].active = false;
                    this.status.channels.active -= 1;
                }
            }
            this.emit('status', this.currentStatus());
        }
        this.reopenDelay = setTimeout(() => {
            this.reopenDelay = null;
            this._open();
        }, this.reopenDelayMs);
        this.reopenDelayMs = Math.min(2 * this.reopenDelayMs, FaucetClient.maxReopenDelayMs);
    }
    _onerror(error) {
        this.emit('error', error);
        this._onclose();
    }
    _handleStart(channelState) {
        if (channelState && !channelState.active) {
            channelState.active = true;
            this.status.channels.active += 1;
            this.emit('status', this.currentStatus());
        }
    }
    _handleStop(channelState) {
        if (channelState && channelState.active) {
            channelState.active = false;
            channelState.snapshot = {};
            this.status.channels.active -= 1;
            this.emit('status', this.currentStatus());
        }
    }
    _onmessage(event) {
        if (!event ||
            typeof event.data !== 'string' ||
            event.data.indexOf(' ') < 0) {
            return;
        }
        const identifier = event.data.substring(0, event.data.indexOf(' '));
        const payloadString = event.data.substring(event.data.indexOf(' ') + 1);
        let payload;
        try {
            payload = JSON.parse(payloadString);
        }
        catch {
            return;
        }
        const channelState = payload.channel ? this.channels[payload.channel] : null;
        const now = Date.now();
        switch (identifier) {
            case 'DATA':
                if (typeof payload.sequence === 'number') {
                    if (payload.sequence !== this.nextExpectedDataSequence) {
                        this._onclose();
                        return;
                    }
                    else {
                        this.nextExpectedDataSequence = payload.sequence + 1;
                    }
                }
                if (typeof payload.data !== 'object') {
                    return;
                }
                if (channelState && channelState.active && channelState.listeners > 0) {
                    const key = payload.data.key;
                    switch (payload.data.op) {
                        case 'CLR':
                            channelState.snapshot = {};
                            break;
                        case 'SET':
                            if (typeof payload.data.key !== 'string' &&
                                typeof payload.data.key !== 'number') {
                                return;
                            }
                            delete payload.data.op;
                            delete payload.data.key;
                            channelState.snapshot[key] = payload.data;
                            break;
                        case 'DEL':
                            if (typeof payload.data.key !== 'string' &&
                                typeof payload.data.key !== 'number') {
                                return;
                            }
                            if (!channelState.snapshot[payload.data.key]) {
                                return;
                            }
                            delete channelState.snapshot[payload.data.key];
                            break;
                        default:
                            for (const prop in payload.data) {
                                channelState.snapshot[prop] = payload.data[prop];
                            }
                            break;
                    }
                    this.emit('data', {
                        channel: payload.channel,
                        snapshot: channelState.snapshot,
                        update: payload.data
                    });
                }
                break;
            case 'ECHO':
                if (typeof payload.timestamp === 'number' && payload.timestamp < now) {
                    this.status.ping = now - payload.timestamp;
                    this.emit('status', this.currentStatus());
                }
                break;
            case 'ERROR':
                this.emit('error', payload);
                break;
            case 'HELLO':
                if (!this.status.authenticated) {
                    this.status.authenticated = true;
                    this.emit('status', this.currentStatus());
                    for (const channel in this.channels) {
                        if (this.channels[channel].listeners > 0) {
                            this._send('START {"channel":"' + channel + '"}');
                        }
                    }
                }
                break;
            case 'START':
                this._handleStart(channelState);
                break;
            case 'STOP':
                this._handleStop(channelState);
                break;
            default:
                // Unknown or unsupported message type?
                break;
        }
    }
    _onopen() {
        this.reopenDelayMs = FaucetClient.initialReopenDelayMs;
        this.status.connected = true;
        this.emit('status', this.currentStatus());
        this._send('HELLO {"format":"insight-v2","token":"' + this.options.token + '"}');
    }
    _open() {
        this.socket = new isomorphic_ws_1.default(this.url);
        this.socket.onclose = this._onclose.bind(this);
        this.socket.onerror = this._onerror.bind(this);
        this.socket.onmessage = this._onmessage.bind(this);
        this.socket.onopen = this._onopen.bind(this);
        this.nextExpectedDataSequence = 1;
    }
    _ping() {
        this._send('ECHO {"timestamp":' + Date.now() + '}');
    }
    _send(message) {
        if (!this.socket || !this.status.connected) {
            return;
        }
        if (this.status.authenticated || /^HELLO/.test(message)) {
            try {
                this.socket.send(message);
            }
            catch {
                // The socket is probably in the process of closing, which means we'll
                // reopen it and resend any needed messages after that, so it's OK to
                // ignore this error.
            }
        }
    }
}
if (typeof window === 'object') {
    window.Faucet = FaucetClient;
}
module.exports = FaucetClient;
//# sourceMappingURL=faucet.js.map